import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';
import { formatDDMMYYDateTime } from '../../utils/dateFormat';
import usePopTransition from '../common/usePopTransition';
import AppSelect from '../common/AppSelect';
import NavIcon from '../common/NavIcons';
import { notificationHref } from '../Notifications/notificationHref';
import './NotificationCenter.css';

const PAGE_SIZE = 10;

/**
 * 'all' is a superset, so it is mutually exclusive with the specific categories: picking one
 * clears 'all', and picking 'all' clears the rest. Deselecting everything falls back to 'all'.
 */
const toggleEvent = (events, id) => {
  const current = Array.isArray(events) ? events : [];
  if (id === 'all') return ['all'];
  const next = current.includes(id)
    ? current.filter((e) => e !== id)
    : [...current.filter((e) => e !== 'all'), id];
  return next.length ? next : ['all'];
};

// Whose a notification is (QA Session 24, Item 202). Offered only to someone who may follow people: for everyone else there is nothing of
// another person's to set apart.
const ABOUT_OPTIONS = [
  { id: 'all', label: 'Everything' },
  { id: 'me', label: 'About me' },
  { id: 'following', label: 'People I follow' }
];
const ABOUT_EMPTY = {
  me: 'Nothing here is about you.',
  following: 'Nothing here is about the people you follow.'
};

const EVENT_OPTIONS = [
  { id: 'all', label: 'Everything' },
  { id: 'feedback', label: 'Feedback' },
  { id: 'dossier', label: 'Dossier changes' },
  { id: 'schedule', label: 'Schedule' }
];

export default function NotificationCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Gates the whole component, not just the Following tab: the router now requires this
  // permission, so without it the bell would render and poll only to 403 every 30 seconds.
  const canFollow = hasPermission(user, 'notifications.follow');
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('inbox');
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState([]);
  const [follows, setFollows] = useState([]);
  const [people, setPeople] = useState([]);
  const [teams, setTeams] = useState([]);
  const [followDraft, setFollowDraft] = useState({ followType: 'person', targetId: '', events: ['all'] });
  const [notifySelf, setNotifySelf] = useState(false);
  const [view, setView] = useState('unread');
  const [about, setAbout] = useState('all');
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState({ unreadCount: 0, readCount: 0, total: 0, limit: PAGE_SIZE, offset: 0, hasMore: false });

  const loadInbox = useCallback(async () => {
    const res = await api.get('/api/notifications', { params: { view, about: canFollow && about !== 'all' ? about : undefined, limit: PAGE_SIZE, offset } });
    const data = res.data || {};
    setUnreadCount(data.unreadCount || 0);
    setItems(data.items || []);
    setPage({
      unreadCount: data.unreadCount || 0,
      readCount: data.readCount || 0,
      total: data.total || 0,
      limit: data.limit || PAGE_SIZE,
      offset: data.offset || 0,
      hasMore: Boolean(data.hasMore)
    });
    // The server clamps an offset that has run past the end (e.g. the page emptied while open).
    if (typeof data.offset === 'number' && data.offset !== offset) setOffset(data.offset);
  }, [view, about, canFollow, offset]);

  const showView = (next) => {
    setView(next);
    setOffset(0);
  };

  const showAbout = (next) => {
    setAbout(next);
    setOffset(0);
  };

  const loadFollows = useCallback(async () => {
    if (!canFollow) return;
    const [followsRes, catalogsRes, agentsRes, prefsRes] = await Promise.all([
      api.get('/api/notifications/follows'),
      api.get('/api/dossier/catalogs').catch(() => ({ data: {} })),
      api.get('/api/dossier/agents').catch(() => ({ data: { agents: [] } })),
      api.get('/api/notifications/preferences').catch(() => ({ data: {} }))
    ]);
    setFollows(followsRes.data.follows || []);
    setTeams(catalogsRes.data.teams || []);
    setPeople(agentsRes.data.agents || []);
    setNotifySelf(prefsRes.data.notifySelf === true);
  }, [canFollow]);

  const toggleNotifySelf = async (next) => {
    setNotifySelf(next);
    try {
      const res = await api.put('/api/notifications/preferences', { notifySelf: next });
      setNotifySelf(res.data.notifySelf === true);
    } catch (_) {
      setNotifySelf(!next);
    }
  };

  useEffect(() => {
    if (!canFollow) return undefined;
    loadInbox().catch(() => {});
    const id = window.setInterval(() => loadInbox().catch(() => {}), 30000);
    return () => window.clearInterval(id);
  }, [loadInbox, canFollow]);

  // The /notifications page tells the bell when it marks something read, so the badge does not wait for the next poll.
  useEffect(() => {
    const refresh = () => { loadInbox().catch(() => {}); };
    window.addEventListener('notifications:changed', refresh);
    return () => window.removeEventListener('notifications:changed', refresh);
  }, [loadInbox]);

  const pop = usePopTransition(open);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      // A select draws its list on the page body (AppSelect, so a table cell cannot clip it), which is
      // outside this panel: picking an option in a Following dropdown must not count as a click outside.
      if (e.target && e.target.closest && e.target.closest('.app-select-list')) return;
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    // Escape closes it like every other menu in the suite; it was the one that ignored it.
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openPanel = () => {
    setOpen((v) => !v);
    loadInbox().catch(() => {});
    if (canFollow) loadFollows().catch(() => {});
  };

  // Where an item leads, if anywhere: one rule for this panel and the /notifications page (Notifications/notificationHref.js).
  const hrefFor = notificationHref;

  // The whole list, with a search and a time filter, is its own page; the panel keeps to the ten at hand.
  const seeAll = () => {
    setOpen(false);
    navigate('/notifications');
  };

  const markRead = async (item) => {
    if (!item.readAt) await api.post(`/api/notifications/${item.id}/read`).catch(() => {});
    loadInbox().catch(() => {});
  };

  const openItem = async (item) => {
    const href = hrefFor(item);
    if (href) {
      setOpen(false);
      navigate(href);
    }
    await markRead(item);
  };

  const addFollow = async () => {
    if (!followDraft.targetId) return;
    await api.post('/api/notifications/follows', followDraft);
    setFollowDraft({ followType: followDraft.followType, targetId: '', events: ['all'] });
    loadFollows().catch(() => {});
  };

  const followLabel = (row) => {
    if (row.followType === 'team') {
      const team = teams.find((t) => String(t.id) === String(row.targetId));
      return team?.name || row.targetId;
    }
    const person = people.find((p) => String(p.id) === String(row.targetId));
    return person?.displayName || person?.fullName || row.targetId;
  };

  // Hooks are all declared above this point, so the early return is safe.
  if (!canFollow) return null;

  return (
    <div className="notif-root" ref={rootRef}>
      <button
        type="button"
        className="suite-icon-btn"
        onClick={openPanel}
        aria-label="Notifications"
        title="Notifications"
        aria-expanded={open}
      >
        <NavIcon name="bell" size={18} />
        {unreadCount > 0 && <span className="notif-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>
      {pop.mounted && (
        <div
          className={`notif-panel ui-pop${pop.closing ? ' is-closing' : ''}`}
          role="dialog"
          aria-label="Notifications"
        >
          <div className="notif-panel__tabs">
            <button type="button" className={`btn btn-sm ${tab === 'inbox' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('inbox')}>Inbox</button>
            {canFollow && (
              <button type="button" className={`btn btn-sm ${tab === 'follows' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('follows')}>Following</button>
            )}
            {page.unreadCount > 0 && tab === 'inbox' && (
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => api.post('/api/notifications/read-all').then(loadInbox)}>
                Mark all read
              </button>
            )}
          </div>
          {tab === 'inbox' && (
            <>
              <div className="notif-views" role="tablist" aria-label="Inbox filter">
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === 'unread'}
                  className={`notif-views__btn${view === 'unread' ? ' is-active' : ''}`}
                  onClick={() => showView('unread')}
                >
                  Unread{page.unreadCount > 0 ? ` (${page.unreadCount})` : ''}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={view === 'read'}
                  className={`notif-views__btn${view === 'read' ? ' is-active' : ''}`}
                  onClick={() => showView('read')}
                >
                  Read{page.readCount > 0 ? ` (${page.readCount})` : ''}
                </button>
              </div>
              {canFollow && (
                <div className="notif-views notif-views--about" role="tablist" aria-label="Whose notifications">
                  {ABOUT_OPTIONS.map((opt) => (
                    <button
                      type="button"
                      role="tab"
                      key={opt.id}
                      aria-selected={about === opt.id}
                      className={`notif-views__btn${about === opt.id ? ' is-active' : ''}`}
                      onClick={() => showAbout(opt.id)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="notif-list">
                {!items.length && (
                  <p className="notif-empty">
                    {canFollow && ABOUT_EMPTY[about]
                      ? ABOUT_EMPTY[about]
                      : (view === 'unread' ? 'You are all caught up.' : 'Nothing read yet.')}
                  </p>
                )}
                {items.map((item) => {
                  const href = hrefFor(item);
                  return (
                    <div className={`notif-item${item.readAt ? '' : ' is-unread'}`} key={item.id}>
                      <button
                        type="button"
                        className="notif-item__main"
                        onClick={() => openItem(item)}
                        title={href ? 'Open' : 'Mark as read'}
                      >
                        <strong>{item.title}</strong>
                        {item.body ? <span>{item.body}</span> : null}
                        <small>
                          {formatDDMMYYDateTime(item.createdAt)}
                          {!href && ' · no longer available'}
                        </small>
                      </button>
                      {!item.readAt && (
                        <button
                          type="button"
                          className="notif-item__read"
                          onClick={() => markRead(item)}
                          title="Mark as read"
                          aria-label={`Mark “${item.title}” as read`}
                        >
                          ✓
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {page.total > page.limit && (
                <div className="notif-pager">
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    disabled={page.offset === 0}
                    onClick={() => setOffset(Math.max(0, page.offset - page.limit))}
                  >
                    ‹ Newer
                  </button>
                  <span className="notif-pager__count">
                    {page.offset + 1}–{page.offset + items.length} of {page.total}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary"
                    disabled={!page.hasMore}
                    onClick={() => setOffset(page.offset + page.limit)}
                  >
                    Older ›
                  </button>
                </div>
              )}
              <div className="notif-foot">
                <button type="button" className="btn btn-sm btn-secondary" onClick={seeAll}>See all</button>
              </div>
            </>
          )}
          {tab === 'follows' && canFollow && (
            <div className="notif-follows">
              <label className="notif-selfnotify">
                <input
                  type="checkbox"
                  checked={notifySelf}
                  onChange={(e) => toggleNotifySelf(e.target.checked)}
                />
                <span>
                  Notify me about my own changes
                  <small>Off by default — turn on to see notifications for edits you make yourself.</small>
                </span>
              </label>
              <div className="notif-follow-form">
                <AppSelect
                  className="app-select"
                  value={followDraft.followType}
                  onChange={(e) => setFollowDraft((p) => ({ ...p, followType: e.target.value, targetId: '' }))}
                >
                  <option value="person">Person</option>
                  <option value="team">Team</option>
                </AppSelect>
                <AppSelect
                  className="app-select"
                  value={followDraft.targetId}
                  onChange={(e) => setFollowDraft((p) => ({ ...p, targetId: e.target.value }))}
                >
                  <option value="">Select…</option>
                  {followDraft.followType === 'person'
                    ? people.map((p) => <option key={p.id} value={p.id}>{p.displayName || p.fullName}</option>)
                    : teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </AppSelect>
                <div className="notif-events" role="group" aria-label="Notify me about">
                  {EVENT_OPTIONS.map((opt) => {
                    const selected = followDraft.events.includes(opt.id);
                    return (
                      <button
                        type="button"
                        key={opt.id}
                        aria-pressed={selected}
                        className={`notif-events__chip${selected ? ' is-selected' : ''}`}
                        onClick={() => setFollowDraft((p) => ({ ...p, events: toggleEvent(p.events, opt.id) }))}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
                <button type="button" className="btn btn-sm btn-primary" onClick={addFollow} disabled={!followDraft.targetId}>Follow</button>
              </div>
              <ul className="notif-follow-list">
                {follows.map((row) => (
                  <li key={row.id}>
                    <span>
                      {row.followType === 'team' ? 'Team' : 'Person'} · {followLabel(row)}
                      <small> {row.events.join(', ')}</small>
                    </span>
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => api.delete(`/api/notifications/follows/${row.id}`).then(loadFollows)}>
                      Unfollow
                    </button>
                  </li>
                ))}
                {!follows.length && <li className="notif-empty">Not following anyone yet.</li>}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
